import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useIntl } from "react-intl";

import SearchSelectDropdown from "src/app/dropdowns/SearchSelectDropdown/SearchSelectDropdown";
import { translateMessage } from "src/app/methods/translateMessage";
import { SearchResultsService } from "src/services";
import {
  ReactSelectOptionWithReactNodeLabel,
  ProjectSearchResult,
} from "src/types";
import useGetProjectsSearchResults from "src/hooks/useGetProjectsSearchResults";
import SelectOption from "src/app/components/SelectOption/SelectOption";
import campaignPlaceholder from "src/images/project-placeholder.svg";

interface ActivityFiltersProjectSearchSelectProps {
  activeWorkspaceUuid: string;
  selectedProjectUuids: string[];
  setSelectedProjectUuids: Dispatch<SetStateAction<string[]>>;
}

export default function ActivityFiltersProjectSearchSelect({
  activeWorkspaceUuid,
  selectedProjectUuids,
  setSelectedProjectUuids,
}: ActivityFiltersProjectSearchSelectProps) {
  const [projectsSearchResultsState, initializeProjectsSearchResultsFetch] =
    useGetProjectsSearchResults();
  const [selectedOptions, setSelectedOptions] = useState<
    ReactSelectOptionWithReactNodeLabel[]
  >([]);

  const intl = useIntl();

  const generateOptions = (
    options: ProjectSearchResult[],
  ): ReactSelectOptionWithReactNodeLabel[] => {
    return options.map((option) => ({
      value: option.wsProjectUuid,
      label: (
        <SelectOption
          name={option.wsProjectName}
          imageSource={
            option.projectCover?.thumbnails.tiny ?? campaignPlaceholder
          }
        />
      ),
    }));
  };

  const handleLoadOptions = async (
    inputValue: string,
    cancelToken: CancelTokenSource,
  ) => {
    const data = await SearchResultsService.getProjects(
      activeWorkspaceUuid,
      [inputValue],
      cancelToken,
    );
    return generateOptions(data);
  };

  useEffect(() => {
    if (selectedProjectUuids.length > 0) {
      initializeProjectsSearchResultsFetch(
        activeWorkspaceUuid,
        selectedProjectUuids,
      );
    }
  }, []);

  useEffect(() => {
    if (projectsSearchResultsState.type === "done") {
      setSelectedOptions(generateOptions(projectsSearchResultsState.data));
    }
  }, [projectsSearchResultsState]);

  useEffect(() => {
    if (selectedProjectUuids.length === 0) {
      setSelectedOptions([]);
    }
  }, [selectedProjectUuids]);

  return (
    <SearchSelectDropdown<ReactSelectOptionWithReactNodeLabel>
      value={selectedOptions}
      handleChange={(newOptions: ReactSelectOptionWithReactNodeLabel[]) => {
        setSelectedOptions(newOptions);
        setSelectedProjectUuids(newOptions.map((option) => option.value));
      }}
      placeholder={translateMessage({
        intl,
        id: "ws_search_campaign",
        defaultMessage: "Type to find a campaign",
      })}
      fetchOptions={handleLoadOptions}
      isMulti
      withTiles
      openMenuOnFocus={false}
    />
  );
}
